import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { cardSrc, parseDum10Rdesc } from './resultParsers';

// Reference owl config for #dum10Slider (same as the AB result sliders):
// slideBy 5, items by WINDOW width — 0:4, 411:5, 500:6, 600+:8. An arrow never
// moves more cards than are in view (phone 4 in view → 4, not 5), so no card is
// skipped (user 2026-10-19).
const SLIDE_BY = 5;
const itemsForWindow = (w) => (w >= 600 ? 8 : w >= 500 ? 6 : w >= 411 ? 5 : 4);
// Slide time, same as the AB 50 / 150 result sliders (AB4Result SLIDE_MS).
const SLIDE_MS = 750;

// Owl Carousel 2 replica (LTR) — owl sizes each item at container / items.
function Dum10Owl({ cards }) {
  const outerRef = useRef(null);
  const [pos, setPos] = useState(0);
  const [perView, setPerView] = useState(8);
  const [itemW, setItemW] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const pv = itemsForWindow(window.innerWidth);
      setPerView(pv);
      const w = outerRef.current?.clientWidth || 0;
      if (w > 0) setItemW(w / pv);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cards.length]);

  const step = Math.min(SLIDE_BY, perView);
  const maxPos = Math.max(0, cards.length - perView);
  const cur = Math.min(pos, maxPos);
  const atStart = cur <= 0;
  const atEnd = cur >= maxPos;
  const navDisabled = cards.length <= perView;

  const prev = useCallback(() => setPos((p) => Math.max(0, Math.min(p, maxPos) - step)), [step, maxPos]);
  const next = useCallback(() => setPos((p) => Math.min(maxPos, p + step)), [step, maxPos]);

  // Finger drag (same as AB4Carousel): the row follows the finger, and on
  // release it stops on the nearest whole card. `pos` can be fractional
  // mid-drag; the arrows step from there.
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);
  const onTouchStart = useCallback((e) => {
    drag.current = { x: e.touches[0].clientX, from: cur };
    setDragging(true);
  }, [cur]);
  const onTouchMove = useCallback((e) => {
    const d = drag.current;
    if (!d || !itemW) return;
    const p = d.from - (e.touches[0].clientX - d.x) / itemW;
    setPos(Math.min(maxPos, Math.max(0, p)));
  }, [itemW, maxPos]);
  const onTouchEnd = useCallback(() => {
    drag.current = null;
    setDragging(false);
    setPos((v) => Math.min(maxPos, Math.max(0, Math.round(v))));
  }, [maxPos]);
  const firstIdx = Math.floor(cur);

  return (
    <div id="dum10Slider" className="text-center ab-slider owl-carousel owl-theme ml-2 mr-2 owl-loaded owl-drag">
      <div
        className="owl-stage-outer"
        ref={outerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{ touchAction: 'pan-y' }}
      >
        <div
          className="owl-stage"
          style={{ transform: `translate3d(${-(cur * itemW)}px, 0px, 0px)`, transition: dragging ? 'none' : `transform ${SLIDE_MS / 1000}s ease`, width: `${cards.length * itemW}px` }}
        >
          {cards.map((code, i) => (
            <div key={i} className={`owl-item${i >= firstIdx && i < firstIdx + perView ? ' active' : ''}`} style={{ width: `${itemW}px` }}>
              <div className="casino-result-cards-item item">
                <img src={cardSrc(code)} alt={code} draggable={false} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={`owl-nav${navDisabled ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}>
          <span aria-label="Previous">‹</span>
        </button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}>
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
}

// Dus ka Dum result — reference markup: col-lg-7 "Cards" heading, owl slider of
// every card except the last, last card big (dum-card); col-lg-5 description.
export default function Dum10Result({ detail }) {
  const allCards = (detail?.card || '').split(',').map((s) => s.trim()).filter(Boolean);
  const prevCards = allCards.slice(0, -1);
  const lastCard = allCards[allCards.length - 1] || '';
  const rdesc = parseDum10Rdesc(detail?.rdesc);

  return (
    <div className="row row5 ab2-result">
      <div className="col-12 col-lg-7">
        <h5 className="text-center">Cards</h5>
        {' '}
        <div className="d-flex p-4">
          <Dum10Owl cards={prevCards} />
          {' '}
          <div className="ml-3">
            <img src={cardSrc(lastCard)} className="dum-card" alt={lastCard} />
          </div>
        </div>
      </div>
      {' '}
      <div className="col-12 col-lg-5">
        {detail?.rdesc && (
          <div className="casino-result-desc">
            <div className="casino-result-desc-item">
              <div>Card</div>
              {' '}
              <div>{rdesc.card}</div>
            </div>
            {' '}
            <div className="casino-result-desc-item">
              <div>Current Total</div>
              {' '}
              <div>{rdesc.currTotal}</div>
            </div>
            {' '}
            <div className="casino-result-desc-item">
              <div>Total</div>
              {' '}
              <div>{rdesc.total}</div>
            </div>
            {' '}
            <div className="casino-result-desc-item">
              <div>Odd/Even</div>
              {' '}
              <div>{rdesc.oddEven}</div>
            </div>
            {' '}
            <div className="casino-result-desc-item">
              <div>Red/Black</div>
              {' '}
              <div>{rdesc.redBlack}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
